import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  Snackbar,
  Alert,
  Paper,
  useTheme,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  Badge,
} from "@mui/material";
import { styled } from "@mui/system";
import Confetti from "react-confetti";
import { useSearchParams } from "react-router-dom";
import SocketHandler from "../../utils/socketHandler";
import { useAuth } from "../../hooks";
import { BingoProgress, BingoCell, BINGO, BingoStatus, getShuffledBoard, BingoBoard, LINE_DEFS, norm} from "./BingoBoard";
// audience hooks
import {
  useJoinBingoSession,
  useMarkBingoCell,
  useLeaveBingoSession,
} from "../../services/stagemode_api/moderator/bingo_api";

interface BingoGameProps {
  userId?: string | number;
  username?: string;
  onBingoComplete?: (userId: string, rank: number) => void;
}

// (Optional) put once at the top of the file
const NF = {
  bg: "#141414",
  panel: "#0b0b0b",
  border: "rgba(255,255,255,0.14)",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.75)",
  red: "#E50914",
  redHover: "#b20710",
};

const BingoGame: React.FC<BingoGameProps> = ({ username }) => {
  const [params] = useSearchParams();

  // from URL
  const bookingIdStr = (params.get("bookingId") || "").replace(/,$/, "");
  const bookingId = Number(bookingIdStr);

  const { userInfo } = useAuth();
  const userId = userInfo?.id;
  const theme = useTheme();

  // game state
  const [board, setBoard] = useState<BingoCell[]>(getShuffledBoard);
  const [currentNumber, setCurrentNumber] = useState<number | null>(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState<string>("");
  const [allCalledNumbers, setAllCalledNumbers] = useState<number[]>([]);
  const [someoneAlreadyWon, setSomeoneAlreadyWon] = useState(false);

  // B-I-N-G-O progression
  const [completedLineIds, setCompletedLineIds] = useState<Set<number>>(new Set());
  const bingoCount = completedLineIds.size; // 0..5 (we cap below)

  // join/session
  const [joined, setJoined] = useState(false);
  const [sessionId, setSessionId] = useState<string | number | null>(null);

  // --- player win & celebration state (modal-based) ---
  const [userWon, setUserWon] = useState(false);                  // true once local user wins this round
  const [showWinCelebration, setShowWinCelebration] = useState(false); // controls 60s modal & confetti
  const [wonThisRound, setWonThisRound] = useState(false);        // persistent status chip until next session starts
  const winTimerRef = useRef<number | null>(null);

  // modals
  const [createdOpen, setCreatedOpen] = useState(false);
  const [createdMeta, setCreatedMeta] = useState<{ session_id?: number | string; host_id?: number | string; status?: string }>({});
  const [startedOpen, setStartedOpen] = useState(false);
  const [startedMeta, setStartedMeta] = useState<{ session_id?: number | string; host_id?: number | string; status?: string; created_at?: string }>({});

  // ---- Hooks: JOIN / LEAVE ----
  const {
    update: joinBingo,
    data: joinData,
    isLoading: isJoining,
  } = useJoinBingoSession({
    params: { bookingId },
    key: ["bingo", "join", bookingId, userId] as const,
  });

  const { update: leaveBingo } = useLeaveBingoSession({
    params: { bookingId },
    key: ["bingo", "leave", bookingId] as const,
  });

  // ---- Hook: Mark cell (session-scoped) ----
  const { update: markCell, isLoading: marking } = useMarkBingoCell({
    params: { sessionId: sessionId ?? ("" as any) },
    key: ["bingo", "mark", sessionId] as const,
  });

  // socket wiring
  const subscribedRef = useRef(false);

  useEffect(() => {
    if (!bookingId || subscribedRef.current) return;
    const socket = SocketHandler.getInstance();

    const onMsg = (msg: any) => {
      console.groupCollapsed("bingo_message");
      console.log(msg);
      console.groupEnd();
      handleBingoEvent(msg);
    };

    socket.subscribeToBingoEvent(String(bookingId), onMsg);
    subscribedRef.current = true;

    return () => {
      socket.unsubscribeToBingoEvent(String(bookingId));
      subscribedRef.current = false;
      if (joined) leaveBingo({}).catch(() => {});
      // cleanup any win timers
      if (winTimerRef.current) {
        window.clearTimeout(winTimerRef.current);
        winTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, joined]);

  // reflect join result → sessionId + board
  useEffect(() => {
    if (!joinData) return;
    const player = (joinData as any)?.data?.player;
    const boardArr: number[] = (joinData as any)?.data?.board;

    const sid = player?.session_id;
    if (sid && !sessionId) setSessionId(sid);

    if (Array.isArray(boardArr) && boardArr.length === 25) {
      const marked = new Set<number>(player?.marked_numbers || []);
      setBoard(boardArr.map((n) => ({ number: n, isClicked: marked.has(n) })));
    }
  }, [joinData, sessionId]);

  const resetForNewRound = () => {
    setBoard(getShuffledBoard());
    setCurrentNumber(null);
    setAllCalledNumbers([]);
    setSomeoneAlreadyWon(false);
    setCompletedLineIds(new Set());
    // clear win flags for the next session
    setUserWon(false);
    setShowWinCelebration(false);
    setWonThisRound(false);
  };

  const resetAllState = async (opts?: { newSessionId?: number | string | null }) => {
  // stop any win timer
  if (winTimerRef.current) {
    window.clearTimeout(winTimerRef.current);
    winTimerRef.current = null;
  }

  // leave current session if joined
  if (joined) {
    try { await leaveBingo({}); } catch {}
  }

  // hard reset everything to a brand new session state
  setJoined(false);
  setSessionId(opts?.newSessionId ?? null);

  setBoard(getShuffledBoard());
  setCurrentNumber(null);
  setAllCalledNumbers([]);
  setSomeoneAlreadyWon(false);

  setCompletedLineIds(new Set());
  setUserWon(false);
  setShowWinCelebration(false);
  setWonThisRound(false);

  // close any transient toasts
  setSnackOpen(false);
};
  // When board changes, recompute completed lines and reveal next letters if any NEW lines are formed
  useEffect(() => {
    const clicked = board.map((c) => c.isClicked);
    const newCompletedIds: number[] = [];

    LINE_DEFS.forEach((line, idx) => {
      const isLine = line.every((i) => clicked[i]);
      if (isLine && !completedLineIds.has(idx)) newCompletedIds.push(idx);
    });

    if (newCompletedIds.length) {
      // update set immutably
      setCompletedLineIds((prev) => {
        const next = new Set(prev);
        newCompletedIds.forEach((id) => next.add(id));
        // Cap to 5 (B I N G O) visually; more lines can exist but we only reveal up to 5
        return next.size > 5 ? new Set(Array.from(next).slice(0, 5)) : next;
      });

      // Show toast for each newly revealed letter (batched as one message)
      const lettersNow = Math.min(5, completedLineIds.size + newCompletedIds.length);
      const justRevealed = BINGO.slice(0, lettersNow).join("");
      setSnackMsg(`Line completed! You now have: ${justRevealed}`);
      setSnackOpen(true);
    }
  }, [board]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- trigger local win when BINGO achieved ---
  useEffect(() => {
    if (!userWon && bingoCount >= 5 && joined) {
      triggerWinSequence();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bingoCount, joined]);

  // Finishes the celebration immediately (user-closed) or from timer
  const finishWinCelebration = async () => {
    if (winTimerRef.current) {
      window.clearTimeout(winTimerRef.current);
      winTimerRef.current = null;
    }
    setShowWinCelebration(false);
    // Leave current session and reset to pre-game state
    if (joined) {
      try { await leaveBingo({}); } catch {}
    }
    setJoined(false);
    setSessionId(null);
    // reset board & numbers but keep "wonThisRound" chip visible
    setBoard(getShuffledBoard());
    setCurrentNumber(null);
    setAllCalledNumbers([]);
    setSomeoneAlreadyWon(true);
    setCompletedLineIds(new Set());
    // keep userWon true; keep wonThisRound true; board hidden because joined=false
  };

  const triggerWinSequence = () => {
    if (userWon) return;
    setUserWon(true);
    setWonThisRound(true);
    setShowWinCelebration(true);

    // Don't use snackbar for the win anymore — the modal is the congrats UI
    setSnackOpen(false);

    // 1-minute celebration; then auto-finish
    if (winTimerRef.current) window.clearTimeout(winTimerRef.current);
    winTimerRef.current = window.setTimeout(() => {
      finishWinCelebration();
    }, 60_000);
  };

  // 2) In handleBingoEvent, update these cases:

const handleBingoEvent = (message: any) => {
  const type = norm(message?.notification_type);
  const sid = message?.session_id ?? message?.id;
  if (sid && !sessionId) setSessionId(sid);

  switch (type) {
    case "bingo:session-created": {
      // NEW: hard reset on a fresh join request so UI is clean for the new session
      resetAllState({ newSessionId: sid }).catch(() => {});
      setCreatedMeta({ session_id: sid, host_id: message?.host_id, status: message?.status });
      setCreatedOpen(true);
      break;
    }
    case "bingo:session-started": {
      // NEW: also hard reset at start to avoid any stale state from prior session
      resetAllState({ newSessionId: sid }).catch(() => {});
      setStartedMeta({
        session_id: sid,
        host_id: message?.host_id,
        status: message?.status,
        created_at: message?.created_at,
      });
      setStartedOpen(true);
      break;
    }
    case "bingo:session-reset": {
      // NEW: ensure fully fresh board and flags on reset
      resetAllState({ newSessionId: sid ?? sessionId }).catch(() => {});
      break;
    }
    case "bingo:session-ended": {
      setCurrentNumber(null);
      setSomeoneAlreadyWon(true);
      leaveBingo({}).catch(() => {});
      break;
    }
    case "bingo:winner-announced": {
      setSomeoneAlreadyWon(true);
      const winnerId =
        message?.winner_id ??
        message?.winner_user_id ??
        message?.winner?.id ??
        message?.winner?.user_id ??
        null;
      if (!userWon && winnerId && userId && Number(winnerId) === Number(userId)) {
        triggerWinSequence();
      }
      break;
    }
    default:
      break;
  }

  // number handling (unchanged)
  const n =
    typeof message?.number === "number"
      ? message.number
      : typeof message?.current_number === "number"
      ? message.current_number
      : undefined;
  if (typeof n === "number" && !Number.isNaN(n)) {
    setCurrentNumber(n);
    setAllCalledNumbers((prev) => (prev.includes(n) ? prev : [...prev, n]));
  }
};


  // JOIN
  const handleJoin = async () => {
    if (!bookingId) return;

    const nameFromLS = localStorage.getItem("stageLink.displayName") || "";
    const guestName =
      (username ?? "").trim() || nameFromLS || `guest-${String(userId || "guest").slice(-6)}`;

    const payload = {
      user_id: Number.isFinite(userId) ? Number(userId) : 0,
      username: guestName,
    };

    try {
      const res: any = await joinBingo(payload);
      const player = res?.data?.player;
      const boardArr: number[] = res?.data?.board;

      const sid = player?.session_id;
      if (sid) setSessionId(sid);

      if (Array.isArray(boardArr) && boardArr.length === 25) {
        const marked = new Set<number>(player?.marked_numbers || []);
        setBoard(boardArr.map((n) => ({ number: n, isClicked: marked.has(n) })));
      }

      setJoined(true);
      setCreatedOpen(false);
      setStartedOpen(false);
      setSnackMsg("Joined the Bingo session!");
      setSnackOpen(true);
    } catch (e: any) {
      if (e?.response?.status === 409) {
        setJoined(true);
        setCreatedOpen(false);
        setStartedOpen(false);
        return;
      }
      setSnackMsg("Failed to join the session. Try again.");
      setSnackOpen(true);
      console.error("[Bingo] join failed:", e?.response?.data || e);
    }
  };

  // Mark cell (only when it matches current number)
  const handleClick = async (index: number) => {
    if (!joined || !sessionId) return;

    const cell = board[index];
    if (cell.isClicked || cell.number !== currentNumber) return;

    try {
      const res: any = await markCell({
        user_id: userId!,
        cell_number: cell.number,
      });

    // Server may return different shapes; handle all:
      const srvCells = res?.data?.cells; // [{ n, marked }, ...]
      const markedNums: number[] = res?.data?.marked_numbers; // [numbers]
      const boardArr: number[] = res?.data?.board; // if they echo the board

      if (Array.isArray(srvCells) && srvCells.length) {
        setBoard(
          srvCells.map((c: any) => ({
            number: Number(c.n),
            isClicked: Boolean(c.marked),
          }))
        );
      } else if (Array.isArray(markedNums)) {
        const marked = new Set<number>(markedNums);
        setBoard((prev) => prev.map((c) => ({ ...c, isClicked: marked.has(c.number) })));
      } else if (Array.isArray(boardArr) && boardArr.length === 25) {
        setBoard(
          boardArr.map((n) => ({
            number: Number(n),
            isClicked: n === cell.number ? true : false,
          }))
        );
      } else {
        // optimistic fallback
        setBoard((prev) => {
          const updated = [...prev];
          updated[index] = { ...prev[index], isClicked: true };
          return updated;
        });
      }

      // clear current number to prevent double clicks
      setCurrentNumber(null);
    } catch (e) {
      console.error("[Bingo] mark failed:", e);
    }
  };

  // local dev helper (hide in prod)
  const simulateNumber = () => {
    const available = board.map((c) => c.number).filter((n) => !allCalledNumbers.includes(n));
    if (!available.length) return;
    const n = available[Math.floor(Math.random() * available.length)];
    setCurrentNumber(n);
    setAllCalledNumbers((p) => [...p, n]);
  };

  const hasWon = bingoCount >= 5;

  return (
    <Box textAlign="center" mt={4} px={2} position="relative">
      {/* Celebration layer: Confetti shown while modal is open */}
      {showWinCelebration && <Confetti width={window.innerWidth} height={window.innerHeight} />}

      {/* Celebration Modal (60s or until user closes) */}
      <Dialog
        open={showWinCelebration}
        onClose={finishWinCelebration}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            color: NF.text,
            background:
              "radial-gradient(900px 420px at 10% -10%, rgba(229,9,20,0.12), transparent 55%), #141414",
            border: `1px solid ${NF.border}`,
            boxShadow: "0 24px 60px rgba(0,0,0,.55)",
            textAlign: "center",
          },
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 2.5, sm: 3 } }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 900,
              letterSpacing: ".2px",
              textShadow: "0 1px 0 rgba(0,0,0,.35)",
            }}
          >
            🎉 You’re the Winner!
          </Typography>
        </Box>

        <DialogContent sx={{ pt: 1, pb: 1.5, px: { xs: 2, sm: 2.5 } }}>
          <Typography variant="body1" sx={{ color: NF.text }}>
            Congratulations! Enjoy the moment — this banner will close automatically in 60 seconds.
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.75, color: NF.muted }}>
            You can close it now to continue browsing. Your win for this round will be saved.
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 2,
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "center",
            background: "linear-gradient(180deg, transparent, rgba(255,255,255,0.03))",
          }}
        >
          <Button
            onClick={finishWinCelebration}
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: 2,
              px: 2.5,
              minHeight: 40,
              background: NF.red,
              boxShadow: "0 10px 28px rgba(229,9,20,.35)",
              "&:hover": { background: NF.redHover },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Header */}
      <Stack spacing={1} alignItems="center" mb={1.5}>
        <Typography variant="h4" fontWeight="bold">
          🎯 Bingo Challenge
        </Typography>

        {/* Persistent win status chip (shows after celebration ends, until next session starts) */}
        {wonThisRound && !joined && !showWinCelebration && (
          <Chip
            color="success"
            label="You won this round"
            sx={{ fontWeight: 700, borderRadius: 2 }}
          />
        )}

        {/* Progress only while board visible */}
        {joined && <BingoProgress count={Math.min(5, bingoCount)} />}
      </Stack>

      {!joined && !showWinCelebration && (
        <Typography variant="subtitle1" color="text.secondary" mb={2}>
          {wonThisRound ? "Waiting for the next round to begin…" : "Waiting for you to join the session…"}
        </Typography>
      )}

      {/* Board after join (hidden during celebration, or after win auto-reset) */}
      {joined && !showWinCelebration && (
        <Paper
          elevation={6}
          sx={{
            p: { xs: 2, sm: 3 },
            maxWidth: 520,
            mx: "auto",
            borderRadius: 4,
            bgcolor: theme.palette.mode === "dark" ? "#0f0f12" : "#fefefe",
            border: "2px solid",
            borderColor: theme.palette.divider,
          }}
        >
          <BingoBoard
            board={board}
            currentNumber={currentNumber}
            onCellClick={handleClick}
            marking={marking}
          />

          <BingoStatus
            currentNumber={currentNumber}
            allCalled={allCalledNumbers}
            linesCompleted={Math.min(5, bingoCount)}
          />
        </Paper>
      )}

      {/* Modals */}
      <Dialog
        open={createdOpen}
        onClose={() => setCreatedOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            color: NF.text,
            background:
              "radial-gradient(900px 420px at 10% -10%, rgba(229,9,20,0.12), transparent 55%), #141414",
            border: `1px solid ${NF.border}`,
            boxShadow: "0 24px 60px rgba(0,0,0,.55)",
          },
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 2, sm: 2.5 } }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 900,
              letterSpacing: ".2px",
              textShadow: "0 1px 0 rgba(0,0,0,.35)",
            }}
          >
            🟣 Bingo session created
          </Typography>
        </Box>

        <DialogContent sx={{ pt: 1, pb: 0.5, px: { xs: 2, sm: 2.5 } }}>
          <Typography variant="body1" sx={{ mb: 1.25, color: NF.text }}>
            The talent just created a Bingo session. Tap “I’m ready” to join the lobby.
          </Typography>

          <Typography variant="body2" sx={{ color: NF.muted }}>
            {createdMeta.session_id ? `Session #${createdMeta.session_id}` : null}
            {createdMeta.host_id ? ` • Host ID: ${createdMeta.host_id}` : null}
            {createdMeta.status ? ` • Status: ${createdMeta.status}` : null}
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 2,
            gap: 1,
            flexWrap: "wrap",
            background: "linear-gradient(180deg, transparent, rgba(255,255,255,0.03))",
          }}
        >
          <Button
            onClick={() => setCreatedOpen(false)}
            variant="outlined"
            sx={{
              color: NF.text,
              borderColor: NF.border,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              px: 2.25,
              minHeight: 40,
              "&:hover": { backgroundColor: "rgba(255,255,255,0.06)", borderColor: NF.border },
            }}
          >
            Later
          </Button>

          <Button
            onClick={handleJoin}
            variant="contained"
            disabled={isJoining || joined}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: 2,
              px: 2.5,
              minHeight: 40,
              background: NF.red,
              boxShadow: "0 10px 28px rgba(229,9,20,.35)",
              "&:hover": { background: NF.redHover },
              "&.Mui-disabled": { background: "rgba(255,255,255,0.16)", color: NF.muted },
            }}
          >
            {joined ? "Joined" : isJoining ? "Joining..." : "I’m ready"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={startedOpen}
        onClose={() => setStartedOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            color: NF.text,
            background:
              "radial-gradient(900px 420px at 10% -10%, rgba(229,9,20,0.12), transparent 55%), #141414",
            border: `1px solid ${NF.border}`,
            boxShadow: "0 24px 60px rgba(0,0,0,.55)",
          },
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 2, sm: 2.5 } }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 900,
              letterSpacing: ".2px",
              textShadow: "0 1px 0 rgba(0,0,0,.35)",
            }}
          >
            🟣 Bingo just started!
          </Typography>
        </Box>

        <DialogContent sx={{ pt: 1, pb: 0.5, px: { xs: 2, sm: 2.5 } }}>
          <Typography variant="body1" sx={{ mb: 1.25, color: NF.text }}>
            Numbers will be called in real time. Join now to play.
          </Typography>

          <Typography variant="body2" sx={{ color: NF.muted }}>
            {startedMeta.session_id ? `Session #${startedMeta.session_id}` : null}
            {startedMeta.host_id ? ` • Host ID: ${startedMeta.host_id}` : null}
            {startedMeta.status ? ` • Status: ${startedMeta.status}` : null}
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 2,
            gap: 1,
            flexWrap: "wrap",
            background: "linear-gradient(180deg, transparent, rgba(255,255,255,0.03))",
          }}
        >
          <Button
            onClick={() => setStartedOpen(false)}
            variant="outlined"
            sx={{
              color: NF.text,
              borderColor: NF.border,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              px: 2.25,
              minHeight: 40,
              "&:hover": { backgroundColor: "rgba(255,255,255,0.06)", borderColor: NF.border },
            }}
          >
            Later
          </Button>

          <Button
            onClick={handleJoin}
            variant="contained"
            disabled={isJoining || joined}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: 2,
              px: 2.5,
              minHeight: 40,
              background: NF.red,
              boxShadow: "0 10px 28px rgba(229,9,20,.35)",
              "&:hover": { background: NF.redHover },
              "&.Mui-disabled": { background: "rgba(255,255,255,0.16)", color: NF.muted },
            }}
          >
            {joined ? "Joined" : isJoining ? "Joining..." : "Join Now"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars (kept for non-win info) */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={2600}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={userWon ? "success" : hasWon ? "success" : "info"}>
          {snackMsg || (hasWon && !showWinCelebration ? "You won the Bingo game!" : "")}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BingoGame;
